import React from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import ImageOutlined from '@mui/icons-material/ImageOutlined';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import {
  Button,
  Input,
  Select,
  Textarea,
  FieldSet,
  Dropzone,
  FileRow,
} from '../../../components/ui';

const RCAForm = ({
  formData,
  handleChange,
  handleFaultLogUpload,
  handleImageUpload,
  handleImageCaptionChange,
  removeFaultLog,
  removeImage,
  loading,
  onCancel,
  onSubmit,
}) => {
  const onDropLog = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      if (file.type === 'text/plain') {
        handleFaultLogUpload(file);
        toast.success('Fault log uploaded');
      } else {
        toast.error('Please upload a .txt file');
      }
    }
  };

  const logDropzone = useDropzone({
    onDrop: onDropLog,
    accept: { 'text/plain': ['.txt'] },
    multiple: false,
  });

  const onDropImages = (acceptedFiles) => {
    const imageFiles = acceptedFiles.filter((file) => file.type.startsWith('image/'));
    if (imageFiles.length > 0) {
      handleImageUpload(imageFiles);
      toast.success(`${imageFiles.length} image${imageFiles.length === 1 ? '' : 's'} uploaded`);
    } else {
      toast.error('Please upload valid image files');
    }
  };

  const imageDropzone = useDropzone({
    onDrop: onDropImages,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.gif'] },
    multiple: true,
  });

  return (
    <form onSubmit={onSubmit} className="space-y-8">
      <FieldSet legend="Incident">
        <div className="grid gap-4 md:grid-cols-2">
          <Input
            id="reportDate"
            name="reportDate"
            type="date"
            label="Report date"
            required
            value={formData.reportDate}
            onChange={handleChange}
          />

          <Select
            id="severityLevel"
            name="severityLevel"
            label="Severity level"
            required
            value={formData.severityLevel}
            onChange={handleChange}
          >
            <option value="">Select severity</option>
            <option value="Minor">Minor</option>
            <option value="Major">Major</option>
            <option value="Critical">Critical</option>
          </Select>

          <Textarea
            id="fault"
            name="fault"
            label="Fault description"
            required
            rows={5}
            className="md:col-span-2"
            value={formData.fault}
            onChange={handleChange}
            placeholder="Describe the fault in detail…"
          />

          <Input
            id="locationAffected"
            name="locationAffected"
            label="Location affected"
            required
            className="md:col-span-2"
            value={formData.locationAffected}
            onChange={handleChange}
            placeholder="Enter location"
          />

          <Input
            id="faultDate"
            name="faultDate"
            type="date"
            label="Date of fault"
            required
            value={formData.faultDate}
            onChange={handleChange}
          />

          <Input
            id="faultTime"
            name="faultTime"
            type="time"
            label="Time of fault"
            required
            value={formData.faultTime}
            onChange={handleChange}
          />
        </div>
      </FieldSet>

      <FieldSet legend="Impact assessment">
        <div className="grid gap-4 md:grid-cols-2">
          <Select
            id="safetyImpact"
            name="safetyImpact"
            label="Safety impact"
            required
            value={formData.safetyImpact}
            onChange={handleChange}
          >
            <option value="">Select…</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </Select>

          <Select
            id="operationalImpact"
            name="operationalImpact"
            label="Operational impact"
            required
            value={formData.operationalImpact}
            onChange={handleChange}
          >
            <option value="">Select…</option>
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
          </Select>

          <Input
            id="serviceImpactDuration"
            name="serviceImpactDuration"
            type="number"
            inputMode="numeric"
            min="0"
            label="Service impact duration"
            hint="In minutes."
            required
            value={formData.serviceImpactDuration}
            onChange={handleChange}
            placeholder="0"
          />

          <Select
            id="stakeholdersAffected"
            name="stakeholdersAffected"
            label="Stakeholders affected"
            required
            value={formData.stakeholdersAffected}
            onChange={handleChange}
          >
            <option value="">Select stakeholder</option>
            <option value="FM08">FM08</option>
            <option value="FM014">FM014</option>
            <option value="FM020">FM020</option>
            <option value="FM030">FM030</option>
            <option value="QDF">QDF</option>
          </Select>
        </div>
      </FieldSet>

      <FieldSet legend="System dynamics">
        <div className="space-y-4">
          <Textarea
            id="operationalCharacteristics"
            name="operationalCharacteristics"
            label="Operational characteristics"
            required
            rows={4}
            value={formData.operationalCharacteristics}
            onChange={handleChange}
            placeholder="Describe operational characteristics…"
          />

          <Textarea
            id="supervisoryState"
            name="supervisoryState"
            label="Supervisory state"
            required
            rows={4}
            value={formData.supervisoryState}
            onChange={handleChange}
            placeholder="Describe supervisory state…"
          />
        </div>
      </FieldSet>

      <FieldSet legend="Supporting documents">
        <div className="space-y-5">
          <Dropzone
            label="Fault log (optional)"
            hint="Accepted format: .txt"
            prompt="Drag a fault log here, or tap to browse"
            dropzone={logDropzone}
          >
            {formData.faultLog && (
              <ul className="mt-3">
                <FileRow
                  icon={DescriptionOutlined}
                  name={formData.faultLog.name}
                  onRemove={removeFaultLog}
                />
              </ul>
            )}
          </Dropzone>

          <Dropzone
            label="Images (optional)"
            hint="Accepted formats: .png, .jpg, .jpeg, .gif"
            prompt="Drag images here, or tap to browse"
            dropzone={imageDropzone}
          >
            {formData.images.length > 0 && (
              <ul className="mt-3 space-y-2">
                {formData.images.map((fileObj, index) => (
                  <FileRow
                    key={index}
                    icon={ImageOutlined}
                    name={fileObj.file.name}
                    onRemove={() => removeImage(index)}
                  >
                    <div className="mt-2">
                      <Input
                        label="Caption"
                        hint="Appears under the image in the generated PDF."
                        value={fileObj.caption || ''}
                        onChange={(e) => handleImageCaptionChange(index, e.target.value)}
                        placeholder="Optional caption"
                      />
                    </div>
                  </FileRow>
                ))}
              </ul>
            )}
          </Dropzone>
        </div>
      </FieldSet>

      <FieldSet legend="Analysis">
        <div className="space-y-4">
          <Textarea
            id="conclusion"
            name="conclusion"
            label="Conclusion"
            required
            rows={5}
            value={formData.conclusion}
            onChange={handleChange}
            placeholder="Enter your conclusion…"
          />

          <Textarea
            id="rootCauseValidation"
            name="rootCauseValidation"
            label="Root cause validation"
            required
            rows={5}
            value={formData.rootCauseValidation}
            onChange={handleChange}
            placeholder="Validate the root cause…"
          />

          <Textarea
            id="recommendations"
            name="recommendations"
            label="Recommendations"
            required
            rows={5}
            value={formData.recommendations}
            onChange={handleChange}
            placeholder="Enter recommendations…"
          />
        </div>
      </FieldSet>

      <div className="z-10 -mx-4 flex gap-3 border-t border-border bg-surface/95 px-4 py-3 backdrop-blur sm:mx-0 sm:justify-end sm:border-0 sm:bg-transparent sm:px-0 sm:backdrop-blur-none">
        <Button type="button" variant="secondary" size="lg" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          type="submit"
          size="lg"
          icon={PictureAsPdfOutlined}
          loading={loading}
          className="flex-1 sm:flex-none"
        >
          {loading ? 'Generating PDF…' : 'Generate PDF'}
        </Button>
      </div>
    </form>
  );
};

export default RCAForm;
